import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { X } from 'lucide-react';
import { useTelaCheia, BotaoTelaCheia } from '../../JanelaModal';
import { MARK, etapaParaAbrir } from '../../../constants';
import { nomeDoCliente } from '../../../data/clients';
import { AlteracaoDoWorkflowProvider } from '../shared/AlteracaoDoWorkflow';

// As telas de cada etapa.
import ProtocoloTab from '../tabs/workflow/ProtocoloTab';
import OPUTab from '../tabs/workflow/OPUTab';
import FIVTab from '../tabs/workflow/FIVTab';
import TETab from '../tabs/workflow/TETab';
import PregnancyTab from '../tabs/workflow/PregnancyTab';

// UMA TELA POR ETAPA.
//
// Havia duas, e a largura da janela escolhia entre elas: abaixo de 768px o
// modal montava uma versão "rápida" de cada etapa, acima montava esta. Não
// eram a mesma tela em dois layouts — eram telas diferentes, com campos
// diferentes e, na FIV, com outro modelo de dado, que foi de onde saiu a
// gravação que zerava vitrificação e DT.
//
// Nenhuma linha do banco veio delas: as 167 doadoras de OPU têm taxa de
// recuperação e hora da aspiração, e as 502 transferências têm corpo lúteo,
// campos que só esta versão grava. Foram escritas para o telefone, e o
// trabalho de campo se faz em tablet ou notebook, que ficam acima do corte.
//
// O que fica no lugar é esta tela se adaptando à largura, não uma segunda
// tela. `compacto` abaixo é densidade de layout, nunca troca de componente.

// Os relatorios do SEBRAE: sao oito documentos para imprimir e assinar, e
// isso se faz sentado, nao no curral.
import RelatoriosVisitaTab from '../tabs/workflow/RelatoriosVisitaTab';

const WorkflowModal = memo(({
  showModal,
  etapaInicial = null,
  service,
  clients = [],
  services = [],
  laboratory = null,
  // A faturinha de entrada deste serviço, se já saiu. Desce até a aba de TE
  // para que quem lança veja ali se a cobrança já existe — a informação
  // morava só no Financeiro, longe de onde a decisão é tomada.
  entradaEmitida = null,
  onSave,
  onClose,
  loading
}) => {
  // O cliente em cima, do cadastro — `client_name` é cópia e envelhece. O
  // contato embaixo: mostrar os dois ajuda a confirmar que se está na ficha
  // certa antes de lançar.
  const cliente = nomeDoCliente(service, clients);
  const contato = clients.find(c => c.id === service?.client_id)?.contact;

  // De onde este serviço herdou a etapa inicial. Quem está com a ficha na mão
  // precisa saber que a OPU não é daqui antes de procurar dados que não existem
  // neste serviço.
  const origem = service?.source_service_id
    ? services.find(s => s.id === service.source_service_id)
    : null;
  const [activeTab, setActiveTab] = useState(null);
  const [compacto, setCompacto] = useState(false);
  /*
   * TELA CHEIA. As tabelas da OPU e da TE têm vinte linhas e dez colunas, e a
   * moldura de 92% x 95% deixava de fora justamente as últimas. Este modal não
   * usa a JanelaModal porque o cabeçalho dele tem abas e o modo compacto, mas
   * a escolha é lembrada do mesmo jeito, e o cabeçalho encolhe igual.
   */
  const [telaCheia, alternarTelaCheia] = useTelaCheia('etapas');
  const [saveError, setSaveError] = useState(null);

  /**
   * Alguém digitou algo e ainda não salvou.
   *
   * Vem de um ouvinte de `input`/`change` no corpo do modal, e não de cada aba
   * avisar que mudou: as cinco guardam o lançamento em estado próprio, com
   * dezenas de handlers, e pedir que cada um marque sujeira seria pedir que
   * ninguém esqueça. O evento sobe de qualquer campo de qualquer aba, que é
   * exatamente a pergunta que interessa — "mexeram em alguma coisa?".
   *
   * Serve só para perguntar antes de descartar. Fechar o modal jogava fora
   * tudo o que não tinha passado pelo botão, calado, e o X fica a um
   * centímetro do Salvar.
   */
  const [sujo, setSujo] = useState(false);
  const marcarAlterado = useCallback(() => setSujo(true), []);
  const [workflowData, setWorkflowData] = useState({
    // Dados flat (compatibilidade com dados antigos)
    protocolo_done: false,
    protocolo_quantity: 0,
    protocolo_date: null,
    protocolo_observations: '',

    opu_done: false,
    opu_quantity: 0,
    opu_date: null,
    opu_observations: '',

    lab_done: false,
    lab_quantity: 0,
    lab_date: null,
    lab_observations: '',

    te_done: false,
    te_quantity: 0,
    te_date: null,
    te_observations: '',

    pregnancy_30d_done: false,
    pregnancy_30d_quantity: 0,
    pregnancy_30d_date: null,
    pregnancy_30d_observations: '',

    pregnancy_60d_done: false,
    pregnancy_60d_quantity: 0,
    pregnancy_60d_date: null,
    pregnancy_60d_observations: '',

    // Dados JSONB (novos - serão implementados nas tabs)
    opu_data: null,
    fiv_data: null,
    te_data: null,
    pregnancy_data: null
  });

  const protocoloRef = useRef(null);
  const opuRef = useRef(null);
  const fivRef = useRef(null);
  const teRef = useRef(null);
  const pregnancyRef = useRef(null);
  const tabRefMap = { protocolo: protocoloRef, opu: opuRef, fiv: fivRef, te: teRef, pregnancy: pregnancyRef };

  /**
   * Densidade do layout, não escolha de tela.
   *
   * Em tela estreita o modal ocupa tudo, aperta os espaçamentos e mostra o
   * contato no cabeçalho. É a MESMA tela: os mesmos campos, a mesma gravação,
   * o mesmo componente montado. Trocar de componente pela largura é o que
   * fazia girar o tablet desmontar a aba e levar junto o que estava digitado.
   */
  useEffect(() => {
    const medir = () => setCompacto(window.innerWidth < 768);
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  // Serviço novo, tela limpa: o que ficou sujo era do serviço anterior.
  useEffect(() => { setSujo(false); }, [showModal, service?.id]);

  /**
   * Fechar com lançamento por salvar pergunta antes.
   *
   * Sem a pergunta, o X descartava calado o que tinha sido digitado — e ele
   * fica a um centímetro do Salvar, numa tela que se usa com luva.
   */
  const fechar = () => {
    if (sujo && !window.confirm(
      'Há lançamento nesta tela que ainda não foi salvo.\n\n'
      + 'Fechar agora descarta o que você digitou. Deseja fechar mesmo assim?'
    )) return;
    onClose();
  };

  // As etapas, na ordem em que acontecem.
  const availableTabs = [
    {
      id: 'protocolo',
      label: 'Protocolo',
      stageCheck: 'protocolo',
      component: ProtocoloTab
    },
    {
      id: 'opu',
      label: 'OPU',
      stageCheck: 'opu',
      component: OPUTab
    },
    {
      id: 'fiv',
      label: 'FIV',
      stageCheck: 'lab',
      component: FIVTab
    },
    {
      id: 'te',
      label: 'TE',
      stageCheck: 'te',
      component: TETab
    },
    {
      id: 'pregnancy',
      label: 'Prenhez',
      stageCheck: 'pregnancy',
      component: PregnancyTab
    },
    {
      id: 'relatorios',
      label: 'Relatórios',
      // Por ultimo e so no SEBRAE: nao e etapa, e a pasta que o programa exige.
      sebraeOnly: true,
      component: RelatoriosVisitaTab
    }
  ];

  // Função para verificar se uma etapa deve ser exibida
  const shouldShowStage = (stageId) => {
    if (!service?.services_offered || !Array.isArray(service.services_offered)) {
      return false;
    }

    const stageMapping = {
      'protocolo': ['protocolo'],
      'opu': ['opu'],
      'lab': ['fiv'], // FIV = laboratório
      'te': ['te'],
      'pregnancy': ['pregnancy_check']
    };

    const requiredServices = stageMapping[stageId] || [];
    return requiredServices.some(serviceType => service.services_offered.includes(serviceType));
  };

  // Filtrar tabs que devem aparecer
  // Aba de programa nao e etapa: nao depende de `services_offered`, depende de
  // o servico ser do programa. As duas condicoes sao diferentes de proposito.
  const visibleTabs = availableTabs.filter(tab =>
    tab.sebraeOnly ? !!service?.sebrae : shouldShowStage(tab.stageCheck));

  // A aba que mostra cada etapa da corrente. DG30 e DG60 dividem a de Prenhez.
  const ABA_DA_ETAPA = {
    protocolo: 'protocolo', opu: 'opu', fiv: 'fiv', te: 'te', dg30: 'pregnancy', dg60: 'pregnancy',
    relatorios: 'relatorios',
  };

  /**
   * Abre na etapa depois da última concluída, e RECALCULA a cada abertura.
   *
   * Antes era `visibleTabs[0]` uma única vez: o serviço abria sempre na OPU, e
   * `!activeTab` fazia com que da segunda vez em diante abrisse na aba em que
   * o usuário tinha parado — inclusive a de outro serviço.
   *
   * `etapaInicial` manda quando vem: quem clicou em "TE sem lançamento há 24d"
   * disse em qual etapa quer cair, e o palpite da corrente nem sempre acerta o
   * mesmo — o serviço pode ter uma etapa mais atrasada do que a que vem depois
   * da última concluída.
   */
  useEffect(() => {
    if (!showModal || !service?.id || visibleTabs.length === 0) return;
    const alvo = ABA_DA_ETAPA[etapaInicial || etapaParaAbrir(service)];
    setActiveTab(visibleTabs.some(t => t.id === alvo) ? alvo : visibleTabs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, service?.id, etapaInicial]);

  // Carregar dados do serviço
  useEffect(() => {
    if (service) {
      setWorkflowData({
        // Dados flat
        protocolo_done: service.protocolo_done || false,
        protocolo_quantity: service.protocolo_quantity || 0,
        protocolo_date: service.protocolo_date || null,
        protocolo_observations: service.protocolo_observations || '',

        opu_done: service.opu_done || false,
        opu_quantity: service.opu_quantity || 0,
        opu_date: service.opu_date || null,
        opu_observations: service.opu_observations || '',

        lab_done: service.lab_done || false,
        lab_quantity: service.lab_quantity || 0,
        lab_date: service.lab_date || null,
        lab_observations: service.lab_observations || '',

        te_done: service.te_done || false,
        te_quantity: service.te_quantity || 0,
        te_date: service.te_date || null,
        te_observations: service.te_observations || '',

        pregnancy_30d_done: service.pregnancy_30d_done || false,
        pregnancy_30d_quantity: service.pregnancy_30d_quantity || 0,
        pregnancy_30d_date: service.pregnancy_30d_date || null,
        pregnancy_30d_observations: service.pregnancy_30d_observations || '',

        pregnancy_60d_done: service.pregnancy_60d_done || false,
        pregnancy_60d_quantity: service.pregnancy_60d_quantity || 0,
        pregnancy_60d_date: service.pregnancy_60d_date || null,
        pregnancy_60d_observations: service.pregnancy_60d_observations || '',

        // Dados JSONB (novos)
        opu_data: service.opu_data || null,
        fiv_data: service.fiv_data || null,
        te_data: service.te_data || null,
        pregnancy_data: service.pregnancy_data || null,

        // Responsável e ajudante de cada etapa. Só entra quando o serviço
        // trouxe a coluna: sem ela no banco, mandar a chave derrubaria o
        // salvamento inteiro por uma equipe que ninguém mexeu.
        ...(service.equipe_das_etapas !== undefined
          ? { equipe_das_etapas: service.equipe_das_etapas || {} }
          : {}),
      });
    }
  }, [service]);

  if (!showModal) return null;


  // Handler para atualizar campos
  const handleUpdate = (field, value) => {
    const quantityFields = ['protocolo_quantity', 'opu_quantity', 'lab_quantity', 'te_quantity', 'pregnancy_30d_quantity', 'pregnancy_60d_quantity'];

    // Função recebe o valor anterior: duas abas mexendo na equipe no mesmo
    // instante (DG30 e DG60 preenchendo o responsável ao abrir) não podem
    // partir da mesma cópia, senão a segunda apaga a primeira.
    setWorkflowData(prev => {
      let processedValue = typeof value === 'function' ? value(prev[field]) : value;
      if (quantityFields.includes(field)) {
        processedValue = value === '' ? null : parseInt(value) || 0;
      }
      return { ...prev, [field]: processedValue };
    });
  };

  /**
   * Salva as etapas, EM FILA, e diz exatamente o que aconteceu com cada uma.
   *
   * A fila é obrigatória: `save_fiv_session` e `save_te_session` tocam as duas
   * a tabela `embryos`, e em paralelo brigam por lock no PostgreSQL. FIV antes
   * de TE porque a TE lê a numeração que a FIV acabou de gravar.
   *
   * O QUE MUDOU AQUI É A HONESTIDADE DO RELATO.
   *
   * O laço parava no primeiro erro e voltava uma frase genérica mandando abrir
   * o console. Só que as etapas ANTERIORES já tinham gravado, e não há como
   * desfazê-las: um erro na TE deixava protocolo, OPU e FIV no banco e o
   * espelho do serviço por escrever, sem nada na tela dizendo isso. A pessoa
   * relia a mesma mensagem e não sabia o que tinha entrado.
   *
   * Agora cada etapa vira uma linha do relato: gravada, sem nada a gravar, ou
   * falhou. O que já entrou é nomeado, porque é o que decide o que fazer
   * depois — tentar de novo custa pouco nas que gravaram, já que a gravação é
   * idempotente, mas saber é o que evita relançar à mão o que já está lá.
   */
  const handleSave = async () => {
    setSaveError(null);
    const tabOrder = ['protocolo', 'opu', 'fiv', 'te', 'pregnancy'];
    const nomeDaEtapa = (id) => availableTabs.find(t => t.id === id)?.label || id;

    const gravadas = [];
    // Gravou, mas com ressalva. A aba devolve `{ aviso }` quando parte do que
    // ela faz não coube no salvamento — hoje é a numeração dos embriões, que
    // não sobe sem sinal. Não é erro: o lançamento está guardado, e esconder a
    // ressalva faria o usuário acreditar que o número foi gravado.
    const avisos = [];

    // Interrompe no primeiro erro de propósito: as etapas seguintes dependem
    // das anteriores, e insistir depois de a FIV falhar gravaria uma TE
    // apontando para embriões que não entraram.
    const parar = (etapa, motivo) => {
      const feitas = gravadas.length > 0
        ? `\n\nJá foi gravado: ${gravadas.join(', ')}. Isso não se perde — ao tentar de novo, essas etapas são regravadas iguais.`
        : '\n\nNada foi gravado ainda.';
      setSaveError(`${etapa}: ${motivo}${feitas}`);
    };

    for (const tabId of tabOrder) {
      const ref = tabRefMap[tabId];
      if (!ref?.current?.save) continue;

      let result;
      try {
        result = await ref.current.save();
      } catch (err) {
        // Falta de campo obrigatório chega aqui. A mensagem da aba é
        // específica ("informe o técnico da TE"), e o nome da etapa vai junto
        // porque quem lê pode estar olhando outra aba.
        parar(nomeDaEtapa(tabId), err.message);
        return;
      }

      if (result === null) {
        parar(nomeDaEtapa(tabId),
          'não foi possível gravar. Se o sinal caiu, o lançamento entra na fila e sobe sozinho; '
          + 'se não, os detalhes estão no console (F12).');
        return;
      }

      // `undefined` é a aba dizendo que não havia o que gravar. Não entra no
      // relato do que foi gravado: contá-la ali seria prometer um registro que
      // não existe.
      if (result !== undefined) gravadas.push(nomeDaEtapa(tabId));
      if (result?.aviso) avisos.push(result.aviso);
    }

    setSujo(false);
    onSave(workflowData, avisos);
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 ${
      telaCheia ? '' : 'sm:p-4'
    }`}>
      <div className={`bg-white w-full h-full flex flex-col ${
        telaCheia
          ? 'max-h-dvh'
          : `sm:rounded-lg sm:max-w-[1800px] sm:w-[92vw] sm:max-h-[95dvh] ${compacto ? 'max-h-dvh' : 'max-h-[95dvh]'}`
      }`}>
        {/* Header - compacto em mobile */}
        {/* ESPAÇO VERTICAL É O RECURSO ESCASSO AQUI.
            O corpo deste modal são tabelas de vinte linhas, e o enfeite fixo
            comia trezentos pixels num tablet de oitocentos: cabeçalho de três
            linhas, barra de abas, e um rodapé com caixa de resumo própria. A
            equipe reclamou, e com razão.

            O que saiu: a linha "Serviços: OPU, FIV, TE", que era a MESMA lista
            da barra de abas logo abaixo, e a caixa do resumo, cujo título
            gastava uma linha para dizer "Resumo". As contagens continuam, numa
            faixa junto dos botões.

            Em TELA CHEIA o cabeçalho vira uma linha só: quem expandiu foi
            buscar altura, e a barra de título de duas linhas a devolvia. */}
        <div className={`flex justify-between border-b ${
          telaCheia ? 'items-center px-3 py-1' : compacto ? 'items-start p-2' : 'items-start p-3 sm:p-4'
        }`}>
          <div className={`flex-1 min-w-0 pr-2 ${telaCheia ? 'flex items-baseline gap-2' : ''}`}>
            <h3 className={`font-semibold text-slate-900 truncate ${
              compacto || telaCheia ? 'text-sm shrink-0 max-w-[40%]' : 'text-base sm:text-xl'
            }`}>
              {cliente}
            </h3>
            {compacto && contato && (
              <p className="text-xs text-slate-500 truncate">{contato}</p>
            )}
            {!compacto && (
              <>
                <p className={telaCheia
                  ? 'text-xs text-slate-500 truncate min-w-0'
                  : 'text-xs sm:text-sm text-slate-600 mt-1'}>
                  {contato && <span className="mr-2">{contato} •</span>}
                  {service?.service_code && (
                    <span className="font-mono text-slate-500 mr-2">#{service.service_code}</span>
                  )}
                  {service?.source_stage && (
                    <span className="mr-2 text-slate-500">
                      {service.source_stage.toUpperCase()} de{' '}
                      <span className="font-mono">
                        #{origem?.service_code || 'serviço removido'}
                      </span>
                      {' •'}
                    </span>
                  )}
                  {service?.date ? new Date(service.date).toLocaleDateString('pt-BR') : 'N/A'} •
                  {' '}
                  {
                    {
                      'por_te': 'Por TE',
                      'por_prenhez': 'Por Prenhez',
                      'diaria': 'Diária',
                      'pacote_completo': 'Pacote Completo',
                      'por_embriao': 'Por Embrião',
                      'personalizado': 'Personalizado'
                    }[service?.billing_model] || 'N/A'
                  }
                </p>
              </>
            )}
          </div>
          <BotaoTelaCheia telaCheia={telaCheia} onAlternar={alternarTelaCheia} className="mr-1" />
          <button
            onClick={fechar}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1"
            disabled={loading}
          >
            <X className={compacto || telaCheia ? 'w-4 h-4' : 'w-5 h-5 sm:w-6 sm:h-6'} />
          </button>
        </div>

        {/* Tab Navigation - mais compacto em tela estreita */}
        <div className="border-b bg-slate-50 overflow-x-auto">
          <nav className={`flex ${compacto ? 'px-0' : 'px-2 sm:px-6'}`} aria-label="Tabs">
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 sm:flex-none font-medium border-b-2 transition-colors whitespace-nowrap
                  ${telaCheia ? 'py-1.5 text-sm' : 'py-2.5 sm:py-3 text-xs sm:text-base'}
                  ${compacto ? 'px-2' : 'px-4 sm:px-6'}
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-white'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content. O ouvinte de digitacao fica aqui: qualquer campo
            de qualquer aba sobe por ele, e e assim que o modal sabe que
            ha lancamento por salvar sem que cada aba precise avisar.

            UMA ROLAGEM SÓ, e é esta. As tabelas das abas tinham a própria,
            de 70% da tela, dentro desta: duas barras, e a do meio prendia a
            roda do mouse. Agora o `thead sticky` delas gruda AQUI, e o
            cabeçalho da tabela que está na tela fica no topo.

            A rolagem lateral também é daqui, do `sm` para cima: caixa com
            overflow-x vira contêiner de rolagem e sequestra o sticky. No
            celular a tabela continua rolando de lado por conta própria —
            senão o formulário acima dela sairia da tela junto. */}
        {/* O que muda por clique (substituir a etapa, arrastar linha) não
            dispara input/change: avisa pelo contexto. */}
        <AlteracaoDoWorkflowProvider value={marcarAlterado}>
        <div
          className="flex-1 overflow-auto"
          onInput={() => setSujo(true)}
          onChange={() => setSujo(true)}
        >
          {visibleTabs.length === 0 && (
            <div className="p-6 text-center text-slate-500">
              <p>Nenhuma etapa disponível para este serviço.</p>
            </div>
          )}
          {visibleTabs.map(tab => {
            const TabComponent = tab.component;
            return (
              <div key={tab.id} className={activeTab === tab.id ? 'h-full' : 'hidden'}>
                <TabComponent
                  ref={tabRefMap[tab.id]}
                  service={service}
                  workflowData={workflowData}
                  onUpdate={handleUpdate}
                  laboratoryId={service?.laboratory_id}
                  laboratory={laboratory}
                  entradaEmitida={entradaEmitida}
                  clients={clients}
                  // As abas ficam TODAS montadas, escondidas por CSS: quem não
                  // recebe isto lê o banco uma vez, na abertura do serviço, e
                  // fica com essa cópia até fechar. A TE usa para reler os
                  // embriões ao ser aberta — a numeração feita na aba de FIV
                  // ao lado não chegava nela de outro jeito.
                  isActive={activeTab === tab.id}
                />
              </div>
            );
          })}
        </div>
        </AlteracaoDoWorkflowProvider>

        {/* Footer - mais compacto em mobile */}
        {!compacto && (
          <div className="border-t p-2 sm:p-3 bg-slate-50">
            {/* Erro de save */}
            {saveError && (
              <div className="mb-2 p-2 bg-slate-50 border border-slate-300 rounded text-xs text-slate-700">
                {MARK.WARN} {saveError}
              </div>
            )}

            {/* As contagens e os botões na MESMA linha. Eram uma caixa branca
                com título próprio acima dos botões, e o título não dizia nada
                que a tela já não deixasse óbvio. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-slate-600 mr-auto">
                {[
                  ['protocolo', 'Receptoras', workflowData.protocolo_quantity],
                  ['opu', 'Óvulos', workflowData.opu_quantity],
                  ['lab', 'Embriões', workflowData.lab_quantity],
                  ['te', 'Transferidos', workflowData.te_quantity],
                  ['pregnancy', 'Prenhez 60D', workflowData.pregnancy_60d_quantity],
                ].filter(([etapa]) => shouldShowStage(etapa)).map(([etapa, rotulo, valor]) => (
                  <span key={etapa}>
                    {rotulo} <span className="font-semibold text-slate-900 tabular-nums">{valor || 0}</span>
                  </span>
                ))}
              </div>

              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-2 px-2 sm:px-3 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-xs sm:text-sm transition-colors"
              >
                {loading ? 'Salvando...' : 'Salvar Workflow'}
              </button>
              <button
                onClick={fechar}
                disabled={loading}
                className="px-2 sm:px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors font-medium text-xs sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Footer mobile - fixo no bottom com botões */}
        {compacto && (
          <div className="border-t bg-slate-50 p-2">
            <div className="flex flex-row gap-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-2 px-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-xs"
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
<button
                onClick={fechar}
                disabled={loading}
                className="px-3 py-2 bg-white text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 font-medium text-xs"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default WorkflowModal;
